import { breakpoints } from './layout'

export enum BreakpointIdentifier {
  xs = 'xs',
  sm = 'sm',
  md = 'md',
  lg = 'lg',
  xl = 'xl',
}

export const mobileBreakpoints = [
  BreakpointIdentifier.xs,
  BreakpointIdentifier.sm,
]
export const tabletBreakpoints = [BreakpointIdentifier.md]
export const desktopBreakpoints = [
  BreakpointIdentifier.lg,
  BreakpointIdentifier.xl,
]

export type Breakpoints = {
  [key in BreakpointIdentifier]: number
}

export const ie11only =
  '(-ms-high-contrast: none) and (-ms-high-contrast: active)'

/**
 * Gets the next breakpoint
 * breakpointNext(sm) => md
 * @param name Breakpoint name
 * @returns Name of the next breakpoint, or null for the last breakpoint.
 */
export const breakpointNext = (
  name: keyof typeof BreakpointIdentifier,
): BreakpointIdentifier | null =>
  Object.values(BreakpointIdentifier)[
    Object.keys(BreakpointIdentifier).indexOf(name) + 1
  ] || null

/**
 *  Returns a min-width with the breakpoint
 * @param breakpoint Name of the breakpoint
 * @returns A min-width query with the breakpoint
 */
export const minWidth = (
  breakpoint: keyof typeof BreakpointIdentifier,
): string =>
  breakpoints[breakpoint] ? `(min-width: ${breakpoints[breakpoint]}px)` : 'all'

/**
 * Returns a max-width for the current breakpoint
 * @param breakpoint Name of the breakpoint
 */
export const maxWidth = (
  breakpoint: keyof typeof BreakpointIdentifier,
): string => {
  const nextBreakpoint = breakpointNext(breakpoint)
  if (!nextBreakpoint) {
    return 'all'
  }

  // The maximum value is calculated as the minimum of the next one less 0.02px
  // to work around the limitations of `min-` and `max-` prefixes and viewports with fractional widths.
  // See https://www.w3.org/TR/mediaqueries-4/#mq-min-max
  // Uses 0.02px rather than 0.01px to work around a current rounding bug in Safari.
  // See https://bugs.webkit.org/show_bug.cgi?id=178261
  const pxs = breakpoints[nextBreakpoint] - 0.02
  return `(max-width: ${pxs}px)`
}

/**
 * Get a breakpoints pixel sizes. This will return a array because devices have multiple breakpoints.
 * @param breakpointOrDevice 'mobile', 'tablet, 'desktop', or a breakpoint defined in type Breakpoints'
 * @returns An array with pixels sorted from small to large
 */
export const breakpointToPixels = (breakpointOrDevice: string): number[] => {
  const devices: { [key: string]: string[] } = {
    mobile: mobileBreakpoints,
    tablet: tabletBreakpoints,
    desktop: desktopBreakpoints,
  }

  // If it is a device, get the breakpoints for the device.
  const breakpoint = devices[breakpointOrDevice] || [breakpointOrDevice]

  // Get the pixels per breakpoint. If it is not a breakpoint it should
  // already be a number, so return that instead.
  const pixels = breakpoint.map((b) =>
    breakpoints[b as BreakpointIdentifier] === undefined
      ? parseFloat(b)
      : breakpoints[b as BreakpointIdentifier],
  )

  // Sort from small to large
  return pixels.sort((a, b) => a - b)
}
